import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../lib/serverSession'
import { effectiveRowShares, isBonusOnlyRow } from '../lib/shareRowDisplay'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface Transaction {
  id: string
  type: 'share_purchase' | 'crypto' | 'bank'
  amount_usd: number
  amount_zar?: number
  shares: number
  is_bonus_only?: boolean
  status: string
  payment_method: string
  reference: string
  created_at: string
  processed_at?: string
  phase_name?: string
}

interface TransactionHistoryProps {
  userId: number
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ userId }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'failed'>('all')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (userId) {
      loadTransactions()
    }
  }, [userId])

  const loadTransactions = async () => {
    setLoading(true)
    setError(null)

    try {
      // Load all transaction types via API
      const response = await fetch(`${API_BASE_URL}/api/user/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId })
      })

      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load transactions')
      }

      const { sharePurchases, cryptoPayments } = result

      // Normalize all transactions
      const normalizedTransactions: Transaction[] = []

      // Collect approved CPT hashes / ids so cancelled submit-placeholder share rows
      // (status=cancelled, payment_method=crypto) that twin a successful credit are hidden.
      const approvedCryptoKeys = new Set<string>()
      ;(cryptoPayments || []).forEach((cp: { id?: string; status?: string; transaction_hash?: string }) => {
        if (String(cp.status || '').toLowerCase() === 'approved') {
          if (cp.id) approvedCryptoKeys.add(String(cp.id).toLowerCase())
          if (cp.transaction_hash) approvedCryptoKeys.add(String(cp.transaction_hash).toLowerCase())
        }
      })
      ;(sharePurchases || []).forEach((sp: { status?: string; transaction_reference?: string }) => {
        if (String(sp.status || '').toLowerCase() === 'active' && sp.transaction_reference) {
          approvedCryptoKeys.add(String(sp.transaction_reference).toLowerCase())
        }
      })
      
      // Add share purchases
      ;(sharePurchases || []).forEach((sp: any) => {
        const status = String(sp.status || 'unknown').toLowerCase()
        const method = String(sp.payment_method || '')
        const ref = String(sp.transaction_reference || '')
        const isSubmitPlaceholder =
          status === 'cancelled' &&
          method.toLowerCase() === 'crypto' &&
          !!ref &&
          approvedCryptoKeys.has(ref.toLowerCase())
        if (isSubmitPlaceholder) return

        normalizedTransactions.push({
          id: `sp-${sp.id}`,
          type: 'share_purchase',
          amount_usd: parseFloat(sp.total_amount) || 0,
          shares: effectiveRowShares(sp),
          is_bonus_only: isBonusOnlyRow(sp),
          status: sp.status || 'unknown',
          payment_method: sp.payment_method || 'Unknown',
          reference: sp.transaction_reference || 'N/A',
          created_at: sp.created_at,
          processed_at: sp.updated_at,
          phase_name: sp.phase_id ? `Phase ${sp.phase_id}` : undefined
        })
      })
      
      // Add crypto payments (avoid duplicates with share_purchases by checking status)
      ;(cryptoPayments || []).forEach((cp: any) => {
        if (cp.status !== 'approved') { // Approved ones are in share_purchases
          normalizedTransactions.push({
            id: `cp-${cp.id}`,
            type: 'crypto',
            amount_usd: parseFloat(cp.amount) || 0,
            shares: parseFloat(cp.shares_to_purchase) || 0,
            status: cp.status || 'pending',
            payment_method: `Crypto (${cp.network || 'Unknown'})`,
            reference: cp.transaction_hash || 'N/A',
            created_at: cp.created_at,
            processed_at: cp.approved_at
          })
        }
      })
      
      // DEPRECATED (v4.3.61): Yoco payments removed
      
      // Sort by created_at descending
      normalizedTransactions.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      
      setTransactions(normalizedTransactions)
    } catch (err) {
      console.error('Error loading transactions:', err)
      setError('Failed to load transaction history')
    } finally {
      setLoading(false)
    }
  }

  // Completed must include share-purchase status "active" (ledger truth after approval).
  const isCompletedStatus = (status: string) =>
    ['approved', 'completed', 'success', 'active'].includes(status.toLowerCase())
  const isFailedStatus = (status: string) =>
    ['rejected', 'failed', 'cancelled', 'declined'].includes(status.toLowerCase())

  // Filter transactions based on active tab
  const filteredTransactions = transactions.filter(t => {
    if (activeTab === 'all') {
      return isCompletedStatus(t.status)
    } else if (activeTab === 'pending') {
      return t.status.toLowerCase() === 'pending'
    } else {
      return isFailedStatus(t.status)
    }
  })

  // Continued in next part...
  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase()
    if (isCompletedStatus(statusLower)) {
      return <span className="px-2 py-1 rounded-full text-sm font-bold bg-green-600 text-white">✓ {status}</span>
    } else if (statusLower === 'pending') {
      return <span className="px-2 py-1 rounded-full text-sm font-bold bg-yellow-600 text-black">⏳ Pending</span>
    } else {
      return <span className="px-2 py-1 rounded-full text-sm font-bold bg-red-600 text-white">✗ {status}</span>
    }
  }

  const getPaymentIcon = (method: string) => {
    if (method.includes('Crypto')) return '₿'
    if (method.includes('Bank')) return '🏦'
    // Yoco removed (v4.3.61)
    return '💰'
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">📊</span>
          <h3 className="text-xl font-bold text-white">Transaction History</h3>
        </div>
        <button
          onClick={loadTransactions}
          disabled={loading}
          className="text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50"
        >
          {loading ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-900 rounded-lg p-1 mb-4">
        {[
          { key: 'all', label: 'Completed', icon: '✓' },
          { key: 'pending', label: 'Pending', icon: '⏳' },
          { key: 'failed', label: 'Failed/Rejected', icon: '✗' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as 'all' | 'pending' | 'failed')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === tab.key
                ? tab.key === 'all' ? 'bg-green-600 text-white'
                  : tab.key === 'pending' ? 'bg-yellow-600 text-black'
                  : 'bg-red-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <span>{tab.icon}</span>
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="text-sm opacity-75">
              ({transactions.filter(t => {
                if (tab.key === 'all') return isCompletedStatus(t.status)
                if (tab.key === 'pending') return t.status.toLowerCase() === 'pending'
                return isFailedStatus(t.status)
              }).length})
            </span>
          </button>
        ))}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-4 mb-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
          <span className="ml-3 text-white">Loading transactions...</span>
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-3">
            {activeTab === 'all' ? '📭' : activeTab === 'pending' ? '✅' : '🎉'}
          </div>
          <p className="text-white">
            {activeTab === 'all'
              ? 'No completed transactions yet'
              : activeTab === 'pending'
                ? 'No pending transactions'
                : 'No failed or rejected transactions'}
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {filteredTransactions.map(tx => (
            <div
              key={tx.id}
              className="bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:border-blue-500/50 transition-colors"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="text-2xl mt-1">{getPaymentIcon(tx.payment_method)}</div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold">${tx.amount_usd.toFixed(2)}</span>
                      {tx.amount_zar && (
                        <span className="text-white text-sm">(R{tx.amount_zar.toFixed(2)})</span>
                      )}
                      <span className="text-gray-500">•</span>
                      <span className="text-yellow-400">{tx.is_bonus_only ? '🎁 ' : ''}{tx.shares.toFixed(2)} shares</span>
                    </div>
                    <div className="text-sm text-white mt-1">
                      {tx.payment_method}
                      {tx.phase_name && <span className="ml-2 text-gray-500">• {tx.phase_name}</span>}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Ref: <span className="font-mono text-white">{tx.reference.slice(0, 20)}{tx.reference.length > 20 ? '...' : ''}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {getStatusBadge(tx.status)}
                  <div className="text-sm text-gray-500 mt-2">
                    {new Date(tx.created_at).toLocaleDateString()}
                  </div>
                  {tx.processed_at && (
                    <div className="text-sm text-green-500">
                      Processed: {new Date(tx.processed_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

